import { Button as PrimeButton } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { Vendor } from "../models/vendor.model";
import { vendorService } from "../service/vendor.service";
import PageHeader from "./PageHeader";

const initialVendor: Partial<Vendor> = {
  name: "",
  description: "",
  contactPerson: "",
  email: "",
  website: "",
  phone: "",
  mobile: "",
  iosOwned: true,
};

const VendorComponent: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [vendor, setVendor] = useState<Partial<Vendor>>(initialVendor);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem("vendorTablePage");
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem("vendorTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sortField, setSortField] = useState(() => {
    const savedSort = localStorage.getItem("vendorTableSortField");
    return savedSort || "name";
  });
  const [sortOrder, setSortOrder] = useState<1 | -1>(() => {
    const savedOrder = localStorage.getItem("vendorTableSortOrder");
    return (savedOrder ? parseInt(savedOrder) : 1) as 1 | -1;
  });

  useEffect(() => {
    const fetchVendors = async () => {
      setListLoading(true);
      try {
        const result = await vendorService.getAllPaginated({
          pageNumber: page,
          pageSize: rows,
          sortField,
          sortOrder: sortOrder === -1 ? "desc" : "asc",
          globalFilter,
        });
        setVendors((result?.items?.flat() ?? []) as Vendor[]);
        setTotalRecords(result?.totalCount ?? 0);
      } catch {
        setVendors([]);
        setTotalRecords(0);
      } finally {
        setListLoading(false);
      }
    };
    fetchVendors();
  }, [success, page, rows, sortField, sortOrder, globalFilter, refreshKey]);

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setVendor({ ...vendor, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      if (vendor.id) {
        const updated = await vendorService.update(vendor.id, vendor as Vendor);
        setVendors((prev) =>
          prev.map((v) => (v.id === vendor.id ? updated : v))
        );
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Vendor updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
      } else {
        const created = await vendorService.create(vendor as Vendor);
        setVendors((prev) => [...prev, created]);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Vendor created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
      }
      setVendor(initialVendor);
      setShowCreateForm(false);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err.message || "Error saving vendor"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row: Vendor) => {
    setVendor(row);
    setShowCreateForm(true);
  };

  const handleDelete = async (row: Vendor) => {
    if (!row.id) return;
    try {
      await vendorService.delete(row.id);
      setVendors((prev) => prev.filter((v) => v.id !== row.id));
      setSuccess(true);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Vendor deleted successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } catch {
      setError("Failed to delete vendor");
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to delete vendor.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    }
  };

  const actionBodyTemplate = (rowData: Vendor, options: any) => (
    <span>
      <button
        className="btn btn-link p-0 me-2"
        title="Edit"
        onClick={() => handleEdit(rowData)}
      >
        <i className="bi bi-pencil"></i>
      </button>
      <button
        className="btn btn-link p-0 text-danger"
        title="Delete"
        onClick={() => handleDelete(rowData)}
      >
        <i className="bi bi-trash"></i>
      </button>
    </span>
  );

  return (
    <div className="content">
      <PageHeader
        title="Vendor Master"
        beforeTitle={
          <PrimeButton
            icon="pi pi-refresh"
            rounded
            text
            severity="secondary"
            onClick={handleRefresh}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Vendors"
          />
        }
      />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center gap-2 my-3">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon>
                    <Icon.Search />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setPage(1);
                      localStorage.setItem("vendorTablePage", "1");
                    }}
                    placeholder="Search"
                    style={{ minWidth: 250 }}
                  />
                </IconField>
                <Button
                  className="btn btn-primary ms-2"
                  onClick={() => {
                    setVendor(initialVendor);
                    setShowCreateForm(true);
                  }}
                >
                  <Icon.PlusCircle className="me-1" /> Create Vendor
                </Button>
              </div>

              <DataTable
                value={vendors}
                dataKey="id"
                paginator
                first={(page - 1) * rows}
                rows={rows}
                totalRecords={totalRecords}
                onPage={(e) => {
                  const newPage = (e.page ?? 0) + 1;
                  const newRows = e.rows;
                  setPage(newPage);
                  setRows(newRows);
                  localStorage.setItem("vendorTablePage", newPage.toString());
                  localStorage.setItem("vendorTableRows", newRows.toString());
                }}
                onSort={(e) => {
                  const newSortField = e.sortField || "name";
                  const newSortOrder = e.sortOrder as 1 | -1;
                  setSortField(newSortField);
                  setSortOrder(newSortOrder);
                  localStorage.setItem("vendorTableSortField", newSortField);
                  localStorage.setItem(
                    "vendorTableSortOrder",
                    newSortOrder.toString()
                  );
                }}
                sortField={sortField}
                sortOrder={sortOrder}
                lazy
                loading={listLoading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage="No vendors found."
                className="grid-table"
              >
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                <Column
                  field="contactPerson"
                  header="Contact Person"
                  sortable
                />
                <Column field="email" header="Email" sortable />
                <Column field="website" header="Website" sortable />
                <Column field="phone" header="Phone" sortable />
                <Column field="mobile" header="Mobile" sortable />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {showCreateForm && (
                <div
                  className="modal show d-block"
                  tabIndex={-1}
                  style={{ background: "rgba(0,0,0,0.3)" }}
                >
                  <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {vendor.id ? "Update Vendor" : "Create Vendor"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowCreateForm(false)}
                        ></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label">Name:</label>
                              <input
                                className="form-control"
                                name="name"
                                value={vendor.name || ""}
                                onChange={handleChange}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Email:</label>
                              <input
                                type="email"
                                className="form-control"
                                name="email"
                                value={vendor.email || ""}
                                onChange={handleChange}
                                required
                              />
                            </div>
                            <div className="col-12">
                              <label className="form-label">Description:</label>
                              <textarea
                                className="form-control"
                                name="description"
                                value={vendor.description || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">
                                Contact Person:
                              </label>
                              <input
                                className="form-control"
                                name="contactPerson"
                                value={vendor.contactPerson || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Website:</label>
                              <input
                                className="form-control"
                                name="website"
                                value={vendor.website || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Phone:</label>
                              <input
                                className="form-control"
                                name="phone"
                                value={vendor.phone || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Mobile:</label>
                              <input
                                className="form-control"
                                name="mobile"
                                value={vendor.mobile || ""}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="form-check mt-2">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="iosOwned"
                                name="iosOwned"
                                checked={vendor.iosOwned ?? false}
                                onChange={(e) =>
                                  setVendor({
                                    ...vendor,
                                    iosOwned: e.target.checked,
                                  })
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="iosOwned"
                              >
                                IOS Owned
                              </label>
                            </div>
                          </div>
                          {error && (
                            <div className="alert alert-danger mt-2">
                              {error}
                            </div>
                          )}
                        </div>

                        <div className="modal-footer">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading
                              ? vendor.id
                                ? "Updating..."
                                : "Creating..."
                              : vendor.id
                              ? "Update Vendor"
                              : "Create Vendor"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowCreateForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorComponent;
